///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Modal from "../Modal";
import { putAssignTruckloadLanes } from "../../api/lanes";
import PanelStateIndicator from "./PanelStateIndicator";
import Spinner from "../icons/Spinner";
import {
  assignRegion,
  fetchTLLaneCountByRegion
} from "../../slices/bidSummarySlice";
import LaneAssignment from "./LaneAssignment";
import messages from "../../helpers/messages";

export const LaneAssignmentModal = ({ bidId, handleClose }) => {
  const dispatch = useDispatch();
  const {
    tlLaneCountByRegion: {
      lanesByRegion: regions,
      isFetching: loading,
      isFailure: error
    }
  } = useSelector(state => state.bidSummary);

  const [saveAssignments, setSaveAssignments] = useState({
    loading: false,
    error: false
  });
  const savableLanes =
    regions && regions.length
      ? regions.filter(r => r.edited && r.newUser).length !== 0
      : false;
  const handleModalClose = () => {
    if (regions && regions.length && regions.filter(r => r.edited).length) {
      dispatch(fetchTLLaneCountByRegion(bidId));
    }
    handleClose();
  };
  const saveAssignmentsDetails = () => {
    if (!saveAssignments.loading) {
      const regionsToAssign = regions
        .filter(r => r.edited && r.newUser)
        .map(({ regionId, newUser: { userId } }) => ({
          bidId: parseInt(bidId),
          regionId,
          userId
        }));
      setSaveAssignments({
        loading: true,
        error: false
      });
      putAssignTruckloadLanes(regionsToAssign)
        .then(() => {
          setSaveAssignments({ loading: false, error: false });
          handleModalClose();
        })
        .catch(() => setSaveAssignments({ loading: false, error: true }));
    }
  };
  const handleUserChange = (regionId, selectedUser) => {
    dispatch(
      assignRegion({
        regionId,
        user: selectedUser
          ? { userId: selectedUser.value, fullName: selectedUser.label }
          : null
      })
    );
  };
  const totalLanes =
    regions && regions.length
      ? regions
          .map(({ laneCount }) => laneCount)
          .reduce((previous, next) => previous + next)
      : 0;
  return (
    <>
      <Modal
        title={messages.systemError.defaultMessage}
        open={saveAssignments.error}
        handleClose={handleModalClose}
        titleClass="modal__title_light uppercase"
        closeButtonClass="modal__close-button mr-24 mt-16"
        bodyClass="border-top-grey p-24"
      >
        <span className="font-size-14">
          {messages.laneAssignmentsErrorMessage.defaultMessage}
        </span>
        <div>
          <button
            onClick={saveAssignmentsDetails}
            className="fr primary-button weight--semibold my-24 px-20 ml-16"
          >
            {messages.tryAgain.defaultMessage}
          </button>
          <button
            onClick={handleModalClose}
            className="fr secondary-button weight--semibold my-24 px-20"
          >
            {messages.exit.defaultMessage}
          </button>
        </div>
      </Modal>

      <Modal
        title={messages.laneAssignments.defaultMessage}
        open={!saveAssignments.error}
        handleClose={handleModalClose}
        titleClass="modal__title_light uppercase"
        closeButtonClass="modal__close-button mr-24 mt-16"
        bodyClass="border-top-grey p-24 bid lane-assignment"
        xLarge={true}
      >
        <div
          style={{ height: loading || error ? 300 : undefined }}
          className={loading || error ? "box" : ""}
        >
          {loading || error ? (
            <PanelStateIndicator
              buttonStyle={{ height: 30, marginTop: 11 }}
              iconSize="32px"
              loading={loading}
              error={!loading && error}
              handleRefresh={() => dispatch(fetchTLLaneCountByRegion(bidId))}
              errorMessage={
                <>
                  <span>{messages.sorrySomethingWentWrong.defaultMessage}</span>
                  <span className="block">
                    {
                      messages.pleaseRefreshToContinueWithLaneAssignments
                        .defaultMessage
                    }
                  </span>
                </>
              }
            />
          ) : (
            regions &&
            regions.map && (
              <>
                <div className="one-whole">
                  <span className="font-size-24">{totalLanes}</span>
                  <span className="font-size-14 pl-8">
                    {messages.totalLanes.defaultMessage}
                  </span>
                </div>
                {regions.map((region, index) => (
                  <LaneAssignment
                    onChange={handleUserChange}
                    key={index}
                    totalLanes={totalLanes}
                    {...region}
                  />
                ))}
                <button
                  data-testid="assign-lanes"
                  disabled={!savableLanes}
                  onClick={saveAssignmentsDetails}
                  className="fr primary-button weight--semibold my-24 px-20"
                >
                  {saveAssignments.loading ? (
                    <Spinner
                      className="spinner"
                      fill="#ffffff"
                      height="16px"
                      width="16px"
                    />
                  ) : (
                    messages.assignLanes.defaultMessage
                  )}
                </button>
              </>
            )
          )}
        </div>
      </Modal>
    </>
  );
};

export default LaneAssignmentModal;
